import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import DestinationTemplate from '../components/DestinationTemplate';
import ExperiencesCategories from '../components/ExperiencesCategories';

const Header = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Title = styled.div`
  font-size: 30px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  color: #385963;
  font-weight: 600;
  margin: 100px 100px 10px;
  gap: 30px;
  height: 500px;

  @media (max-width: 768px) {
    font-size: 28px;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 20px;
    text-align: center;
  }
`;

const NavContainer = styled.nav`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 30px 20px;
  background-color: transparent;
  position: relative;
  width: 100%;
  align-items: center;
`;

const NavTabs = styled.ul`
  display: flex;
  list-style: none;
  align-items: center;
  gap: 20px;
  padding: 0;

  li {
    margin-right: 10px;

    a {
      text-decoration: none;
      color: #385963;
      font-size: 16px;
      font-weight: 500;
      padding: 10px 0;
      margin: 0 10px;
      position: relative;
      transition: color 0.4s;

      &:hover {
        color: #91abb3;
      }

      &::before {
        content: "";
        position: absolute;
        width: 0;
        height: 2px;
        bottom: 0;
        left: 50%;
        background-color:  #91abb3;
        transition: all 0.4s;
      }

      &:hover::before {
        width: 100%;
        left: 0;
      }

      &.active {
        color: #91abb3;
        font-weight: 700;

        &::before {
          width: 100%;
          left: 0;
        }
      }
    }
  }

  
  @media (max-width: 1200px) {
    flex-direction: column;
    align-items: center;

   li a {
      font-size: 14px;
      white-space: normal;
      text-align: center;
    }
  }

  @media (max-width: 992px) {
   li a {
      font-size: 14px;
      white-space: normal;
      text-align: center;
    }
  }

  @media (max-width: 768px) {
  gap:10px;

  li a {
      font-size: 14px;
      white-space: normal;
      text-align: center;
    }
  }

  @media (max-width: 576px) {
  gap:8px;

  li  a {
      font-size: 10px;
      white-space: normal;
      text-align: center;
    }
  }
`;


const CategoryInfo = styled.div`
  text-align: center;
  margin-top: 20px;
  max-width: 800px;
  color: #385963;

  h2 {
    margin-bottom: 10px;
  }

  p {
    margin: 0;
  }

  @media (max-width: 768px) {
    max-width: 90%;
    margin-bottom: 40px;
    h2 {
      font-size: 22px;
    }
    p {
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    max-width: 80%;
     margin-bottom: 20px;
    h2 {
      font-size: 16px;
    }
    p {
      font-size: 10px;
    }
  }
`;

const MainBody = styled.div``;

const Experiences = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const location = useLocation();
  const { state } = location;
  const [activeComponent, setActiveComponent] = useState(state?.activeComponent || '');
  const [categoryId, setCategoryId] = useState(state?.categoryId || '');
  const selectedLanguage = sessionStorage.getItem('selectedLanguage') || 'en';

  useEffect(() => {
    if (state?.activeComponent) setActiveComponent(state.activeComponent);
    if (state?.categoryId) setCategoryId(state.categoryId);
  }, [state]);

  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const handleExperienceNavigation = (component, newCategoryId) => {
    navigate('/experiences', { state: { activeComponent: component, categoryId: newCategoryId } });
  };

  const [experienceCategories, setExperienceCategories] = useState([]);

  useEffect(() => {
    const fetchExperienceCategories = async () => {
      try {
        const response = await fetch(`${BASE_URL}ExperienceCategory`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setExperienceCategories(data);
      } catch (error) {
        console.error('Error fetching experience categories:', error);
      }
    };

    fetchExperienceCategories();
  }, [BASE_URL]);

  return (
    <div>
      <Header>
        <NavContainer>
          <NavTabs>
            {experienceCategories.map((category) => (
              <li key={category.id} onClick={() => handleExperienceNavigation('mainExperiences', category.id)}>
                <Link
                  to="#"
                  className={category.id === categoryId ? 'active' : ''}
                >
                  {category.name[selectedLanguage]}
                </Link>
              </li>
            ))}
          </NavTabs>
          {activeComponent !== 'ExperienceTemplate' && (
            <hr style={{ margin: '20px 0', borderTop: '2px solid #538392', width: '90%', placeSelf: 'center' }} />
          )}

           {categoryId && activeComponent === 'mainExperiences' && (
            <CategoryInfo>
              {(() => {
                const selectedCategory = experienceCategories.find(
                  (cat) => cat.id === categoryId
                );
                if (!selectedCategory) return null;
                return (
                  <>
                    <h2>{selectedCategory.name[selectedLanguage]}</h2>
                    <p>{selectedCategory.description?.[selectedLanguage]}</p>
                  </>
                );
              })()}
            </CategoryInfo>
          )}
        </NavContainer>
      </Header>

      <MainBody>
        {activeComponent === '' && (
          <Title>
            <div></div>
            <div>{i18n.t('startChooseCategory')}</div>
          </Title>
        )}
        {activeComponent === 'mainExperiences' && <ExperiencesCategories categoryId={categoryId} />}
        {activeComponent === 'ExperienceTemplate' && <DestinationTemplate />}
      </MainBody>
    </div>
  );
};

export default Experiences;
